<template>
  <div>
    <TheSonyBar ref="sonyBar" />
    <TheNavBar ref="navBar" />
    <ClientOnly>
      <AstrobotPopIn v-if="displayAstrobotPopIn" />
      <TheIncentive
        v-else-if="displayIncentive"
        :incentive-type="incentiveType"
      />
    </ClientOnly>

    <slot />
    <TheFooter />
    <CookieBanner
      :need-cookie="needCookie && !isTempUser"
      @change-cookie="changeCookie"
    />
    <!-- a Google sign-up to finalize: nothing floating over the fields of its form -->
    <DiscordBubble v-if="!isTempUser" />
    <ModalsContainer />
  </div>
</template>

<script setup lang="ts">
import type { TheNavBar, TheSonyBar } from '#components'
import { joinURL, withQuery } from 'ufo'
import { ModalsContainer, useVfm } from 'vue-final-modal'
import { isAstrobotContestRunning } from '~/utils/helpers'

const runtimeConfig = useRuntimeConfig()
const route = useRoute()

// the canonical is the path, never the tracking or cache parameters (?utm_source, ?fbclid, ?v…) that made every
// shared link a duplicate for Google; only a page number beyond 1 stays (paginated lists set theirs, useLinkPrevNext)
const link = computed(() => {
  const page = Number(route.query.page)

  return [
    {
      rel: 'canonical',
      href: withQuery(
        joinURL(runtimeConfig.public.baseUrl, route.path),
        page > 1 ? { page } : {},
      ),
    },
  ]
})

useHead({
  link,
})

// deep pages of a paginated list (Bing crawled /actualites-communautes?page=4826, 250-700 KB each, 0 impression):
// kept out of the index, their links still followed; every post is in the sitemap. A page that sets its own robots
// (empty list) comes after the layout and wins
const NOINDEX_AFTER_PAGE = 50

// social tags every page shares; a page sets its own og:type (article…) and its title, description and image
useSeoMeta({
  robots: () =>
    Number(route.query.page) > NOINDEX_AFTER_PAGE ? 'noindex, follow' : undefined,
  ogUrl: () => link.value[0].href,
  ogType: 'website',
  ogSiteName: 'We Are PlayStation',
  ogLocale: 'fr_FR',
  twitterCard: 'summary_large_image',
})

const sonyBar = ref<InstanceType<typeof TheSonyBar> | null>(null)
const navBar = ref<InstanceType<typeof TheNavBar> | null>(null)

useComputeModalHeight(sonyBar, navBar)

const router = useRouter()
const vfm = useVfm()
const needCookie = ref(false)
// a Google sign-up being finalized: the banner (above every modal, 2/3 of a phone screen) covered the form and took
// its first tap on « Enregistrer », mostly in the Instagram in-app browser, never consented; it shows once finalized
const isTempUser = useIsTempUser()
const displayAstrobotPopIn = ref(true)

const allowCookie = useLocalStorage('allow-cookie', {
  cookie1: false,
  cookie2: false,
  cookie3: false,
})

router.afterEach((to, from) => {
  if (import.meta.server) return

  if (to.name !== from.name || to.name !== 'index') {
    vfm.closeAll()
  }

  needCookie.value = !allowCookie.value.cookie1

  checkDisplayAstrobotPopIn()
})

const crossContentStore = useCrossContentStore()
const { crossContent } = storeToRefs(crossContentStore)

if (route.path === '/page/cookies') {
  needCookie.value = true
}

const connectedBannerId = computed(
  () => crossContent.value?.bannerInfosConnected?.id,
)
const { authenticated } = useAuthenticated()

const authenticatedIncentiveIdCookie = useCookie<number | undefined>(
  'display-in-connected',
)

const isAuthenticatedIncentiveActive = computed(() => {
  if (
    !crossContent.value?.bannerInfosConnected?.display_start ||
    !crossContent.value?.bannerInfosConnected?.display_end
  ) {
    return false
  }

  const start = new Date(crossContent.value?.bannerInfosConnected.display_start)
  const end = new Date(crossContent.value.bannerInfosConnected.display_end)
  if (countdownS(start) <= 0 && countdownS(end) >= 0) {
    return true
  }
  return false
})

const displayAuthenticatedIncentive = computed(() => {
  if (!authenticated.value || needCookie.value) return false
  if (!connectedBannerId.value) return false
  if (connectedBannerId.value === authenticatedIncentiveIdCookie.value) {
    return false
  }

  return isAuthenticatedIncentiveActive.value
})

watch([displayAuthenticatedIncentive], (newDisplayAuthenticatedIncentive) => {
  if (newDisplayAuthenticatedIncentive) {
    authenticatedIncentiveIdCookie.value = undefined
  }
})

const publicIncentiveIdCookie = useCookie<number | undefined>('display-in')

const displayPublicIncentive = computed(() => {
  if (needCookie.value) return false
  if (authenticated.value) return false
  if (route.name === 'inscription') return false
  if (!crossContent.value?.bannerInfos?.id) return false

  return crossContent.value.bannerInfos.id !== publicIncentiveIdCookie.value
})

watch(displayPublicIncentive, (newDisplayPublicIncentive) => {
  if (newDisplayPublicIncentive) {
    publicIncentiveIdCookie.value = undefined
  }
})

const displayIncentive = computed(() => {
  return (
    (displayPublicIncentive.value || displayAuthenticatedIncentive.value) &&
    !isAstrobotContestRunning()
  )
})

const incentiveType = computed(() => {
  if (displayAuthenticatedIncentive.value) return 'connected'
  return 'unconnected'
})

const changeCookie = () => {
  needCookie.value = false

  checkDisplayAstrobotPopIn()
}

const checkDisplayAstrobotPopIn = () => {
  displayAstrobotPopIn.value =
    publicIncentiveIdCookie.value !== 3 &&
    isAstrobotContestRunning() &&
    route.name !== 'inscription' &&
    !needCookie.value
}

onMounted(async () => {
  await nextTick()

  if (route.path === '/page/cookies') {
    needCookie.value = true
  } else {
    needCookie.value = !allowCookie.value.cookie1
  }

  checkDisplayAstrobotPopIn()
})
</script>

<style>
*,
*::before,
*::after {
  box-sizing: border-box;
  margin: 0;
}

.button--green {
  display: inline-block;
  padding: 10px 30px;
  color: #3b8070;
  text-decoration: none;
  border: 1px solid #3b8070;
  border-radius: 4px;
}

.button--green:hover {
  color: #fff;
  background-color: #3b8070;
}

.button--grey {
  display: inline-block;
  margin-left: 15px;
  padding: 10px 30px;
  color: #35495e;
  text-decoration: none;
  border: 1px solid #35495e;
  border-radius: 4px;
}

.button--grey:hover {
  color: #fff;
  background-color: #35495e;
}
</style>
